import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { createOrder, getOrder } from 'utils/api/cartApis';
import { getUserSession } from 'utils/api/sessionApis';
import { deleteOrderFromStore, saveOrder } from 'utils/cartHelpers';
import { getStore } from 'utils/helpers/localStorageHelpers';

import CartContext from '../Cart.context';

export type CartProviderProps = {
  children: React.ReactNode;
  /**
   * If true, the cart will use the saved order from the browser storage
   * to fetch the order from the API if it exists.
   * @default true
   */
  syncOrderWithBrowserStorage?: boolean;
};

/**
 * This component is meant to be defined near the root of the application
 * so that all components can access the cart state.
 */
function CartProvider(props: CartProviderProps): JSX.Element {
  const { children, syncOrderWithBrowserStorage = true } = props;

  const [openCartParam] = useQueryParam('openCart', BooleanParam);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartErrors, setCartErrors] = useState<string[]>([]);

  const openCart = () => {
    setIsCartOpen(true);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const [orderId, setOrderId] = useState<number | undefined | null>(
    // undefined means that the orderId is not yet fetched from either the browser storage or the API
    syncOrderWithBrowserStorage ? undefined : null,
  );

  /**
   * TODO: entire payload should be utilized in the useUserContext hook
   */
  const { data: sessionResponse } = useQuery(
    ['user-session'],
    () => getUserSession(),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
  const userData = sessionResponse?.data;
  const userDataExists = Boolean(userData);

  /**
   * Loads the current order from browser storage and sets the orderId state
   */
  useEffect(() => {
    if (!syncOrderWithBrowserStorage) {
      return;
    }

    const browserStorage = getStore();

    const currentOrder =
      browserStorage.current_order && JSON.parse(browserStorage.current_order);
    const orderId = currentOrder?.id;

    if (orderId) {
      setOrderId(orderId);
    } else {
      setOrderId(null);
    }
  }, [syncOrderWithBrowserStorage]);

  /**
   * open the cart if the page has set the query parameter to open the cart
   */
  useEffect(() => {
    if (openCartParam) {
      setIsCartOpen(true);
    }
  }, [openCartParam]);

  /**
   * Fetch the current order from the API with the defined orderId
   */
  const {
    data: fetchedOrderResponse,
    isError,
    refetch: refetchOrder,
    isLoading,
    isRefetching,
  } = useQuery([`orders-${orderId}`], () => getOrder(orderId || 0), {
    enabled: Boolean(orderId) && userDataExists,
    retry: false,
    onSuccess: (response) => {
      if (syncOrderWithBrowserStorage) {
        // TODO: save the orderId instead of the full order and save only on create
        saveOrder(response);
      }
    },
  });

  /**
   * Creates a new order in the API and saves it in the browser storage and
   * saves the orderId in the local state
   */
  const createOrderData = userData
    ? userData.logged_in
      ? { user_id: userData.user_id ?? -1 }
      : { token: userData.token }
    : /* this way we can catch this in datadog. we shouldn't be calling the endpoint anyways due to the enabled flag logic */
      { token: 'missing token' };

  useQuery(['create-order'], () => createOrder(createOrderData), {
    enabled: (isError || orderId === null) && userDataExists,
    retry: 0,
    onSuccess: (response) => {
      setOrderId(response.data.id);
    },
  });

  const resetOrder = () => {
    deleteOrderFromStore();

    setOrderId(null);
  };

  return (
    <CartContext.Provider
      value={{
        openCart,
        closeCart,
        isCartOpen,
        order: fetchedOrderResponse?.data,
        refetchOrder,
        isFetchingOrder: isLoading || isRefetching,
        setCartErrors,
        cartErrors,
        resetOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export default CartProvider;
